import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useSearchParams } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import Router from "./Router";
import i18n from "../lib/i18n/config";
import { AlertContainer } from "../components/Alert";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MaintenanceGuard } from "../features/maintenance";

import { ProfileModal } from "../features/profile/components/modals/ProfileModal";
import { GDPRBanner } from "../components/gdpr";
import { SetCurrencyTimezoneModal } from "../features/auth/components/SetCurrencyTimezoneModal";
import { useProfileModalStore } from "../stores/profileModalStore";
import { ThemeHandler } from "../components/ThemeHandler";
import { OnboardingManager } from "../components/onboarding/OnboardingManager";
import { GlobalSocketManager } from "../components/GlobalSocketManager";
import { useOnboardingStore } from "../stores/onboardingStore";
import { useAuthStore } from "../stores/authStore";
import { useQuery } from "@tanstack/react-query";
import { userService } from "../lib/api/users";


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const QueryParamHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openProfileModal, setActiveTab } = useProfileModalStore();

  useEffect(() => {
    const modal = searchParams.get("modal");
    const tab = searchParams.get("tab");

    if (modal === "profile") {
      if (tab) {
        setActiveTab(tab);
      }
      openProfileModal();

      const newParams = new URLSearchParams(searchParams);
      newParams.delete("modal");
      newParams.delete("tab");
      newParams.delete("setup_result");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, openProfileModal, setActiveTab, setSearchParams]);

  return null;
};

import { usePageTracking, useVisitorPresence } from "../lib/tracking";

const Tracker = () => {
  usePageTracking();
  useVisitorPresence();
  return null;
};

import { GoogleOAuthProvider } from '@react-oauth/google';

const OnboardingInitializer = () => {
  const { isAuthenticated, setUser } = useAuthStore();
  const { init } = useOnboardingStore();

  // Fetch the latest profile to ensure onboarding status is synced from DB
  const { data: profileResponse, isSuccess } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => userService.getProfile(),
    enabled: isAuthenticated,
    staleTime: Infinity, // Only fetch once per session unless invalidated
  });

  // ONLY initialize the onboarding store once the network request is done
  // This ensures no stale status from localStorage is used.
  useEffect(() => {
    if (isSuccess && profileResponse?.data?.user) {
      const freshUser = profileResponse.data.user;
      // Update both stores with the fresh data from DB
      setUser(freshUser as any);
      init(freshUser.onboardingStatus);
    }
  }, [isSuccess, profileResponse, setUser, init]);

  return null;
};



function App() {
  /* 
     We use a placeholder default to prevent the GoogleOAuthProvider from crashing 
     if the environment variable is missing (e.g. during local dev setup). 
     The actual login button will hide itself if the ID is missing.
  */
  // Use the ID from config or environment, defaulting to the placeholder only if truly undefined
  const GOOGLE_CLIENT_ID = window.__DASHBAL_CONFIG__?.googleClientId || 
    process.env.GOOGLE_CLIENT_ID || 'missing-client-id.apps.googleusercontent.com';


  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <MaintenanceGuard>
          <BrowserRouter>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <ThemeHandler />
              <AlertContainer />
              <ConfirmDialog />
              <GlobalSocketManager />

              <ProfileModal />
              <SetCurrencyTimezoneModal />
              <QueryParamHandler />
              <Tracker />
              <Router />
              <OnboardingManager />
              <OnboardingInitializer />
              <GDPRBanner />
            </GoogleOAuthProvider>
          </BrowserRouter>
        </MaintenanceGuard>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;

